<script setup>
import {
  ref,
  reactive,
  computed,
  nextTick,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  inbox: { type: Object, required: true },
  template: { type: Object, default: null },
});

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();
const TB = 'WHATSAPP_TEMPLATES.BUILDER';

const isEditMode = computed(() => !!props.template);
const isSubmitting = ref(false);
const isUploading = ref(false);

const form = reactive({
  name: '',
  language: 'pt_BR',
  category: 'MARKETING',
  headerType: 'none',
  headerText: '',
  headerHandle: '',
  headerFileName: '',
  bodyText: '',
  footerText: '',
  buttons: [],
  variableExamples: {},
});

// Initialize form from template (edit mode)
if (props.template) {
  form.name = props.template.name || '';
  form.language = props.template.language || 'pt_BR';
  form.category = props.template.category || 'MARKETING';

  const components = props.template.components || [];
  const header = components.find(c => c.type === 'HEADER');
  const body = components.find(c => c.type === 'BODY');
  const footer = components.find(c => c.type === 'FOOTER');
  const buttons = components.find(c => c.type === 'BUTTONS');

  if (header) {
    form.headerType = header.format?.toLowerCase() || 'text';
    if (header.format === 'TEXT') form.headerText = header.text || '';
  }
  if (body) form.bodyText = body.text || '';
  if (footer) form.footerText = footer.text || '';
  if (buttons) {
    form.buttons = (buttons.buttons || []).map(btn => ({ ...btn }));
  }
}

// Variables extracted from body text
const bodyVariables = computed(() => {
  const matches = form.bodyText.match(/\{\{(\d+)\}\}/g) || [];
  return [...new Set(matches)].sort();
});

const headerVariables = computed(() => {
  if (form.headerType !== 'text') return [];
  const matches = form.headerText.match(/\{\{(\d+)\}\}/g) || [];
  return [...new Set(matches)];
});

// Preview body with examples substituted + WhatsApp formatting
const previewBody = computed(() => {
  let text = form.bodyText;
  bodyVariables.value.forEach(v => {
    const key = v.replace(/[{}]/g, '');
    const example = form.variableExamples[`body_${key}`] || v;
    text = text.replace(v, example);
  });
  return text;
});

// GK-HUB: Render WhatsApp formatting (*bold*, _italic_) as HTML for preview
const previewBodyHtml = computed(() => {
  let text = previewBody.value;
  text = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  text = text.replace(/```([^`]+)```/g, '<code class="wa-code">$1</code>');
  text = text.replace(/\*([^*]+)\*/g, '<b>$1</b>');
  text = text.replace(/_([^_]+)_/g, '<i>$1</i>');
  text = text.replace(/~([^~]+)~/g, '<s>$1</s>');
  text = text.replace(/\n/g, '<br>');
  return text;
});

const previewHeader = computed(() => {
  let text = form.headerText;
  headerVariables.value.forEach(v => {
    const key = v.replace(/[{}]/g, '');
    const example = form.variableExamples[`header_${key}`] || v;
    text = text.replace(v, example);
  });
  return text;
});

// Validation
const nameValid = computed(() => /^[a-z][a-z0-9_]*$/.test(form.name));
const bodyValid = computed(
  () => form.bodyText.length > 0 && form.bodyText.length <= 1024
);
const footerValid = computed(() => form.footerText.length <= 60);
const headerTextValid = computed(
  () => form.headerType !== 'text' || form.headerText.length <= 60
);

const canSubmit = computed(() => {
  if (!isEditMode.value && (!form.name || !nameValid.value)) return false;
  if (!bodyValid.value) return false;
  if (!footerValid.value) return false;
  if (!headerTextValid.value) return false;
  return true;
});

// Button management
const quickReplyCount = computed(
  () => form.buttons.filter(b => b.type === 'QUICK_REPLY').length
);
const urlCount = computed(
  () => form.buttons.filter(b => b.type === 'URL').length
);
const phoneCount = computed(
  () => form.buttons.filter(b => b.type === 'PHONE_NUMBER').length
);

const showBtnMenu = ref(false);

const addButton = type => {
  const btn = { type, text: '' };
  if (type === 'URL') btn.url = '';
  if (type === 'PHONE_NUMBER') btn.phone_number = '';
  form.buttons.push(btn);
  showBtnMenu.value = false;
};

const removeButton = index => {
  form.buttons.splice(index, 1);
};

const insertVariable = () => {
  const existing = form.bodyText.match(/\{\{(\d+)\}\}/g) || [];
  const nextNum = existing.length + 1;
  form.bodyText += `{{${nextNum}}}`;
};

// GK-HUB: Bold formatting toolbar — wraps selected text with *bold* (WhatsApp format)
const bodyTextarea = ref(null);
const applyBold = () => {
  const el = bodyTextarea.value;
  if (!el) return;
  const start = el.selectionStart;
  const end = el.selectionEnd;
  if (start === end) return; // no selection
  const before = form.bodyText.slice(0, start);
  const selected = form.bodyText.slice(start, end);
  const after = form.bodyText.slice(end);
  form.bodyText = `${before}*${selected}*${after}`;
  nextTick(() => {
    el.focus();
    el.setSelectionRange(start + 1, end + 1);
  });
};
const applyItalic = () => {
  const el = bodyTextarea.value;
  if (!el) return;
  const start = el.selectionStart;
  const end = el.selectionEnd;
  if (start === end) return;
  const before = form.bodyText.slice(0, start);
  const selected = form.bodyText.slice(start, end);
  const after = form.bodyText.slice(end);
  form.bodyText = `${before}_${selected}_${after}`;
  nextTick(() => {
    el.focus();
    el.setSelectionRange(start + 1, end + 1);
  });
};
const applyStrikethrough = () => {
  const el = bodyTextarea.value;
  if (!el) return;
  const start = el.selectionStart;
  const end = el.selectionEnd;
  if (start === end) return;
  const before = form.bodyText.slice(0, start);
  const selected = form.bodyText.slice(start, end);
  const after = form.bodyText.slice(end);
  form.bodyText = `${before}~${selected}~${after}`;
  nextTick(() => {
    el.focus();
    el.setSelectionRange(start + 1, end + 1);
  });
};
const applyMonospace = () => {
  const el = bodyTextarea.value;
  if (!el) return;
  const start = el.selectionStart;
  const end = el.selectionEnd;
  if (start === end) return;
  const before = form.bodyText.slice(0, start);
  const selected = form.bodyText.slice(start, end);
  const after = form.bodyText.slice(end);
  form.bodyText = `${before}\`\`\`${selected}\`\`\`${after}`;
  nextTick(() => {
    el.focus();
    el.setSelectionRange(start + 3, end + 3);
  });
};
// GK-HUB: end formatting toolbar

// GK-HUB: Preview sticky scroll — uses JS because ancestor has overflow:auto which breaks CSS sticky
const previewWrapper = ref(null);
const builderContainer = ref(null);
const handleScroll = () => {
  if (!previewWrapper.value || !builderContainer.value) return;
  const container =
    builderContainer.value.closest('.overflow-auto') ||
    builderContainer.value.closest('.settings');
  if (!container) return;
  const containerRect = container.getBoundingClientRect();
  const builderRect = builderContainer.value.getBoundingClientRect();
  const previewEl = previewWrapper.value;
  const offset = 20;

  if (builderRect.top < containerRect.top + offset) {
    const maxScroll = builderRect.height - previewEl.offsetHeight;
    const scrolled = containerRect.top + offset - builderRect.top;
    const translateY = Math.min(Math.max(0, scrolled), maxScroll);
    previewEl.style.transform = `translateY(${translateY}px)`;
  } else {
    previewEl.style.transform = 'translateY(0)';
  }
};
onMounted(() => {
  const scrollParent =
    builderContainer.value?.closest('.overflow-auto') || window;
  scrollParent.addEventListener('scroll', handleScroll, { passive: true });
});
onBeforeUnmount(() => {
  const scrollParent =
    builderContainer.value?.closest('.overflow-auto') || window;
  scrollParent.removeEventListener('scroll', handleScroll);
});
// GK-HUB: end Preview sticky scroll

// Media upload
const handleFileUpload = async event => {
  const file = event.target.files[0];
  if (!file) return;

  isUploading.value = true;
  try {
    const formData = new FormData();
    formData.append('file', file);
    const result = await store.dispatch('inboxes/uploadTemplateMedia', {
      inboxId: props.inbox.id,
      formData,
    });
    form.headerHandle = result.handle;
    form.headerFileName = file.name;
    useAlert(t(`${TB}.UPLOAD_SUCCESS`));
  } catch (e) {
    const msg = e?.response?.data?.error || t(`${TB}.UPLOAD_ERROR`);
    useAlert(msg);
  } finally {
    isUploading.value = false;
  }
};

// Build components for API
const buildComponents = () => {
  const components = [];

  // Header
  if (form.headerType === 'text' && form.headerText) {
    const comp = { type: 'HEADER', format: 'TEXT', text: form.headerText };
    if (headerVariables.value.length > 0) {
      comp.example = {
        header_text: headerVariables.value.map(v => {
          const key = v.replace(/[{}]/g, '');
          return form.variableExamples[`header_${key}`] || 'example';
        }),
      };
    }
    components.push(comp);
  } else if (['image', 'video', 'document'].includes(form.headerType)) {
    const comp = {
      type: 'HEADER',
      format: form.headerType.toUpperCase(),
    };
    if (form.headerHandle) {
      comp.example = { header_handle: [form.headerHandle] };
    }
    components.push(comp);
  }

  // Body
  const bodyComp = { type: 'BODY', text: form.bodyText };
  if (bodyVariables.value.length > 0) {
    bodyComp.example = {
      body_text: [
        bodyVariables.value.map(v => {
          const key = v.replace(/[{}]/g, '');
          return form.variableExamples[`body_${key}`] || 'example';
        }),
      ],
    };
  }
  components.push(bodyComp);

  // Footer
  if (form.footerText) {
    components.push({ type: 'FOOTER', text: form.footerText });
  }

  // Buttons
  if (form.buttons.length > 0) {
    components.push({
      type: 'BUTTONS',
      buttons: form.buttons.map(btn => {
        const b = { type: btn.type, text: btn.text };
        if (btn.type === 'URL') {
          b.url = btn.url;
          if (btn.url?.includes('{{')) {
            b.example = [btn.url.replace(/\{\{\d+\}\}/, 'example')];
          }
        }
        if (btn.type === 'PHONE_NUMBER') b.phone_number = btn.phone_number;
        return b;
      }),
    });
  }

  return components;
};

// Submit
const handleSubmit = async () => {
  if (!canSubmit.value || isSubmitting.value) return;
  isSubmitting.value = true;

  try {
    if (isEditMode.value) {
      await store.dispatch('inboxes/updateTemplate', {
        inboxId: props.inbox.id,
        templateId: props.template.id,
        components: buildComponents(),
      });
      useAlert(t(`${TB}.UPDATE_SUCCESS`));
    } else {
      await store.dispatch('inboxes/createTemplate', {
        inboxId: props.inbox.id,
        template: {
          name: form.name,
          language: form.language,
          category: form.category,
          components: buildComponents(),
        },
      });
      useAlert(t(`${TB}.SUBMIT_SUCCESS`));
    }
    emit('close');
  } catch (e) {
    const msg =
      e?.response?.data?.error ||
      (isEditMode.value ? t(`${TB}.UPDATE_ERROR`) : t(`${TB}.SUBMIT_ERROR`));
    useAlert(msg);
  } finally {
    isSubmitting.value = false;
  }
};

const UPLOAD_CONFIG = {
  image: {
    accept: 'image/jpeg,image/png',
    hint: t(`${TB}.UPLOAD_IMAGE_HINT`),
    label: t(`${TB}.UPLOAD_IMAGE`),
    icon: '\uD83D\uDDBC',
  },
  video: {
    accept: 'video/mp4',
    hint: t(`${TB}.UPLOAD_VIDEO_HINT`),
    label: t(`${TB}.UPLOAD_VIDEO`),
    icon: '\uD83C\uDFAC',
  },
  document: {
    accept: 'application/pdf',
    hint: t(`${TB}.UPLOAD_DOCUMENT_HINT`),
    label: t(`${TB}.UPLOAD_DOCUMENT`),
    icon: '\uD83D\uDCC4',
  },
};
</script>

<!-- eslint-disable vue/no-bare-strings-in-template vue/no-static-inline-styles -->
<template>
  <div>
    <!-- Header -->
    <div class="flex justify-between items-start mb-4 gap-4 flex-wrap">
      <div>
        <h2 class="text-xl font-semibold text-n-slate-12 tracking-tight">
          {{ isEditMode ? t(`${TB}.EDIT_TITLE`) : t(`${TB}.CREATE_TITLE`) }}
        </h2>
        <p class="text-[13px] text-n-slate-10 mt-0.5">
          {{
            isEditMode ? t(`${TB}.EDIT_SUBTITLE`) : t(`${TB}.CREATE_SUBTITLE`)
          }}
        </p>
      </div>
      <button
        class="text-[13px] font-medium text-n-blue-11 hover:text-n-blue-12 bg-transparent border-none cursor-pointer py-1"
        @click="$emit('close')"
      >
        &#x2190; {{ t(`${TB}.BACK_TO_LIST`) }}
      </button>
    </div>

    <!-- Edit warning -->
    <div
      v-if="isEditMode && template.status === 'APPROVED'"
      class="flex items-center gap-2 px-3.5 py-2.5 mb-4 rounded-lg text-[13px] bg-amber-500/8 border border-amber-500/20 text-amber-500"
    >
      &#x26A0; {{ t(`${TB}.EDIT_WARNING`) }}
    </div>

    <!-- Side-by-side layout -->
    <div ref="builderContainer" class="wa-builder-layout">
      <!-- LEFT: Form -->
      <div class="flex flex-col gap-4 pb-20">
        <!-- Basic info -->
        <div class="bg-n-solid-2 border border-n-weak rounded-[10px] p-5">
          <div class="wa-section-title">
            <span class="w-1.5 h-1.5 rounded-full bg-n-blue-11" />
            {{ t(`${TB}.BASIC_INFO`) }}
          </div>
          <div class="wa-form-row">
            <div class="wa-form-group">
              <label class="text-xs font-semibold text-n-slate-10">
                {{ t(`${TB}.NAME_LABEL`) }}
              </label>
              <input
                v-model="form.name"
                class="wa-input"
                type="text"
                :placeholder="t(`${TB}.NAME_PLACEHOLDER`)"
                :disabled="isEditMode"
                :class="{ 'wa-input--error': form.name && !nameValid }"
                @input="form.name = form.name.replace(/\s/g, '_').toLowerCase()"
              />
              <span class="text-[11px] text-n-slate-9">
                {{ t(`${TB}.NAME_HINT`) }}
              </span>
            </div>
            <div class="wa-form-group">
              <label class="text-xs font-semibold text-n-slate-10">
                {{ t(`${TB}.LANGUAGE_LABEL`) }}
              </label>
              <div class="isp-select-wrap">
                <select
                  v-model="form.language"
                  class="isp-select w-full"
                  :disabled="isEditMode"
                >
                  <option value="pt_BR">pt_BR</option>
                  <option value="en">en</option>
                  <option value="en_US">en_US</option>
                  <option value="es">es</option>
                </select>
                <span class="isp-select-arrow i-lucide-chevron-down" />
              </div>
            </div>
            <div class="wa-form-group">
              <label class="text-xs font-semibold text-n-slate-10">
                {{ t(`${TB}.CATEGORY_LABEL`) }}
              </label>
              <div class="isp-select-wrap">
                <select
                  v-model="form.category"
                  class="isp-select w-full"
                  :disabled="isEditMode"
                >
                  <option value="MARKETING">Marketing</option>
                  <option value="UTILITY">Utility</option>
                </select>
                <span class="isp-select-arrow i-lucide-chevron-down" />
              </div>
            </div>
          </div>
        </div>

        <!-- Header -->
        <div class="bg-n-solid-2 border border-n-weak rounded-[10px] p-5">
          <div class="wa-section-title">
            <span class="w-1.5 h-1.5 rounded-full bg-n-blue-11" />
            {{ t(`${TB}.HEADER_SECTION`) }}
            <span
              class="font-normal normal-case tracking-normal text-n-slate-9 text-[11px] opacity-60"
            >
              — {{ t(`${TB}.OPTIONAL`) }}
            </span>
          </div>
          <div class="wa-form-group">
            <label class="text-xs font-semibold text-n-slate-10">
              {{ t(`${TB}.HEADER_TYPE_LABEL`) }}
            </label>
            <div class="isp-select-wrap">
              <select v-model="form.headerType" class="isp-select w-full">
                <option value="none">{{ t(`${TB}.HEADER_TYPE_NONE`) }}</option>
                <option value="text">{{ t(`${TB}.HEADER_TYPE_TEXT`) }}</option>
                <option value="image">
                  {{ t(`${TB}.HEADER_TYPE_IMAGE`) }}
                </option>
                <option value="video">
                  {{ t(`${TB}.HEADER_TYPE_VIDEO`) }}
                </option>
                <option value="document">
                  {{ t(`${TB}.HEADER_TYPE_DOCUMENT`) }}
                </option>
              </select>
              <span class="isp-select-arrow i-lucide-chevron-down" />
            </div>
          </div>

          <!-- Text header -->
          <div v-if="form.headerType === 'text'" class="wa-form-group">
            <input
              v-model="form.headerText"
              class="wa-input"
              type="text"
              :placeholder="t(`${TB}.HEADER_TEXT_PLACEHOLDER`)"
            />
            <div class="flex justify-between items-center">
              <span class="text-[11px] text-n-slate-9">{{
                t(`${TB}.BODY_HINT`)
              }}</span>
              <span
                class="text-[11px] text-n-slate-9"
                :class="{ 'text-amber-500': form.headerText.length > 50 }"
              >
                {{ form.headerText.length }} / 60
              </span>
            </div>
            <!-- Header variable examples -->
            <div
              v-if="headerVariables.length > 0"
              class="flex flex-col gap-1.5 mt-1.5"
            >
              <div
                v-for="v in headerVariables"
                :key="'h_' + v"
                class="flex items-center gap-2"
              >
                <span class="wa-var-tag">{{ v }}</span>
                <input
                  v-model="
                    form.variableExamples['header_' + v.replace(/[{}]/g, '')]
                  "
                  class="wa-input wa-input--sm flex-1"
                  type="text"
                  :placeholder="t(`${TB}.VARIABLE_EXAMPLE`, { variable: v })"
                />
              </div>
            </div>
          </div>

          <!-- Media upload -->
          <div
            v-if="['image', 'video', 'document'].includes(form.headerType)"
            class="wa-form-group"
          >
            <label
              class="wa-upload-area"
              :class="{ 'wa-upload--done': form.headerHandle }"
            >
              <input
                type="file"
                :accept="UPLOAD_CONFIG[form.headerType]?.accept"
                style="display: none"
                @change="handleFileUpload"
              />
              <template v-if="form.headerHandle">
                <span class="text-2xl text-n-teal-11">&#x2713;</span>
                <span class="text-[13px] text-n-teal-11 font-semibold">{{
                  form.headerFileName
                }}</span>
              </template>
              <template v-else-if="isUploading">
                <span class="wa-spin text-2xl">&#x21BB;</span>
              </template>
              <template v-else>
                <span class="text-3xl opacity-25">{{
                  UPLOAD_CONFIG[form.headerType]?.icon
                }}</span>
                <span class="text-[13px] text-n-slate-10">{{
                  UPLOAD_CONFIG[form.headerType]?.label
                }}</span>
                <span class="text-[11px] text-n-slate-9">{{
                  UPLOAD_CONFIG[form.headerType]?.hint
                }}</span>
              </template>
            </label>
          </div>
        </div>

        <!-- Body -->
        <div class="bg-n-solid-2 border border-n-weak rounded-[10px] p-5">
          <div class="wa-section-title">
            <span class="w-1.5 h-1.5 rounded-full bg-n-blue-11" />
            {{ t(`${TB}.BODY_SECTION`) }}
            <span
              class="font-bold normal-case tracking-normal text-n-ruby-11 text-[10px] ml-1"
            >
              {{ t(`${TB}.BODY_REQUIRED`) }}
            </span>
          </div>
          <div class="wa-form-group">
            <label class="text-xs font-semibold text-n-slate-10">
              {{ t(`${TB}.BODY_LABEL`) }}
            </label>
            <!-- GK-HUB: Formatting toolbar (Bold, Italic) -->
            <div
              class="flex items-center gap-1 px-2 py-1 border border-n-weak border-b-0 rounded-t-lg bg-n-solid-3"
            >
              <button
                type="button"
                class="px-2 py-0.5 rounded text-xs font-bold text-n-slate-11 hover:bg-n-blue-3 hover:text-n-blue-11 transition-colors cursor-pointer bg-transparent border-none"
                title="Bold (*texto*)"
                @click="applyBold"
              >
                B
              </button>
              <button
                type="button"
                class="px-2 py-0.5 rounded text-xs italic text-n-slate-11 hover:bg-n-blue-3 hover:text-n-blue-11 transition-colors cursor-pointer bg-transparent border-none"
                title="Italic (_texto_)"
                @click="applyItalic"
              >
                I
              </button>
              <button
                type="button"
                class="px-2 py-0.5 rounded text-xs line-through text-n-slate-11 hover:bg-n-blue-3 hover:text-n-blue-11 transition-colors cursor-pointer bg-transparent border-none"
                title="Strikethrough (~texto~)"
                @click="applyStrikethrough"
              >
                S
              </button>
              <button
                type="button"
                class="px-2 py-0.5 rounded text-[11px] font-mono text-n-slate-11 hover:bg-n-blue-3 hover:text-n-blue-11 transition-colors cursor-pointer bg-transparent border-none"
                title="Monospace (```texto```)"
                @click="applyMonospace"
              >
                &lt;/&gt;
              </button>
              <span class="w-px h-4 bg-n-weak mx-1" />
              <button
                type="button"
                class="px-2 py-0.5 rounded text-[11px] text-n-slate-11 hover:bg-n-blue-3 hover:text-n-blue-11 transition-colors cursor-pointer bg-transparent border-none"
                @click="insertVariable"
              >
                + {{ t(`${TB}.ADD_VARIABLE`) }}
              </button>
            </div>
            <!-- GK-HUB: end formatting toolbar -->
            <textarea
              ref="bodyTextarea"
              v-model="form.bodyText"
              class="wa-input wa-textarea !rounded-t-none !border-t-0"
              rows="4"
              :class="{ 'wa-input--error': form.bodyText.length > 1024 }"
            />
            <div class="flex justify-between items-center">
              <span class="text-[11px] text-n-slate-9">{{
                t(`${TB}.BODY_HINT`)
              }}</span>
              <span
                class="text-[11px] text-n-slate-9"
                :class="{
                  'text-amber-500': form.bodyText.length > 900,
                  'text-n-ruby-11': form.bodyText.length > 1024,
                }"
              >
                {{ form.bodyText.length }} / 1024
              </span>
            </div>
          </div>
          <!-- Variable examples -->
          <div
            v-if="bodyVariables.length > 0"
            class="flex flex-col gap-1.5 mt-1.5"
          >
            <div
              v-for="v in bodyVariables"
              :key="'b_' + v"
              class="flex items-center gap-2"
            >
              <span class="wa-var-tag">{{ v }}</span>
              <input
                v-model="
                  form.variableExamples['body_' + v.replace(/[{}]/g, '')]
                "
                class="wa-input wa-input--sm flex-1"
                type="text"
                :placeholder="t(`${TB}.VARIABLE_EXAMPLE`, { variable: v })"
              />
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="bg-n-solid-2 border border-n-weak rounded-[10px] p-5">
          <div class="wa-section-title">
            <span class="w-1.5 h-1.5 rounded-full bg-n-blue-11" />
            {{ t(`${TB}.FOOTER_SECTION`) }}
            <span
              class="font-normal normal-case tracking-normal text-n-slate-9 text-[11px] opacity-60"
            >
              — {{ t(`${TB}.OPTIONAL`) }}
            </span>
          </div>
          <div class="wa-form-group">
            <input
              v-model="form.footerText"
              class="wa-input"
              type="text"
              :placeholder="t(`${TB}.FOOTER_PLACEHOLDER`)"
            />
            <div class="flex justify-between items-center">
              <span class="text-[11px] text-n-slate-9">{{
                t(`${TB}.FOOTER_HINT`)
              }}</span>
              <span
                class="text-[11px] text-n-slate-9"
                :class="{ 'text-amber-500': form.footerText.length > 50 }"
              >
                {{ form.footerText.length }} / 60
              </span>
            </div>
          </div>
        </div>

        <!-- Buttons -->
        <div class="bg-n-solid-2 border border-n-weak rounded-[10px] p-5">
          <div class="wa-section-title">
            <span class="w-1.5 h-1.5 rounded-full bg-n-blue-11" />
            {{ t(`${TB}.BUTTONS_SECTION`) }}
            <span
              class="font-normal normal-case tracking-normal text-n-slate-9 text-[11px] opacity-60"
            >
              — {{ t(`${TB}.OPTIONAL`) }}, {{ t(`${TB}.BUTTONS_MAX`) }}
            </span>
          </div>

          <div v-if="form.buttons.length > 0" class="flex flex-col gap-2 mb-3">
            <div
              v-for="(btn, idx) in form.buttons"
              :key="idx"
              class="wa-button-item"
            >
              <span
                class="wa-btn-type"
                :class="{
                  'wa-btn-type--qr': btn.type === 'QUICK_REPLY',
                  'wa-btn-type--url': btn.type === 'URL',
                  'wa-btn-type--phone': btn.type === 'PHONE_NUMBER',
                }"
              >
                {{
                  btn.type === 'QUICK_REPLY'
                    ? 'QR'
                    : btn.type === 'URL'
                      ? 'URL'
                      : 'TEL'
                }}
              </span>
              <input
                v-model="btn.text"
                class="wa-input wa-input--sm flex-1"
                :placeholder="t(`${TB}.BUTTON_TEXT_PLACEHOLDER`)"
              />
              <input
                v-if="btn.type === 'URL'"
                v-model="btn.url"
                class="wa-input wa-input--sm flex-1"
                :placeholder="t(`${TB}.BUTTON_URL_PLACEHOLDER`)"
              />
              <input
                v-if="btn.type === 'PHONE_NUMBER'"
                v-model="btn.phone_number"
                class="wa-input wa-input--sm flex-1"
                :placeholder="t(`${TB}.BUTTON_PHONE_PLACEHOLDER`)"
              />
              <button
                class="bg-transparent border-none text-n-slate-9 hover:text-n-ruby-11 cursor-pointer text-sm p-0.5 shrink-0"
                @click="removeButton(idx)"
              >
                &#x2715;
              </button>
            </div>
          </div>

          <div class="relative">
            <button
              class="btn-outline-xs"
              :disabled="form.buttons.length >= 10"
              @click="showBtnMenu = !showBtnMenu"
            >
              + {{ t(`${TB}.ADD_BUTTON`) }} &#x25BC;
            </button>
            <div v-if="showBtnMenu" class="wa-btn-menu">
              <button
                class="wa-btn-option"
                :disabled="quickReplyCount >= 3"
                @click="addButton('QUICK_REPLY')"
              >
                <span class="wa-btn-type wa-btn-type--qr">QR</span>
                {{ t(`${TB}.BUTTON_QUICK_REPLY`) }}
                <span class="text-[11px] text-n-slate-9 ml-auto"
                  >{{ quickReplyCount }}/3</span
                >
              </button>
              <button
                class="wa-btn-option"
                :disabled="urlCount >= 2"
                @click="addButton('URL')"
              >
                <span class="wa-btn-type wa-btn-type--url">URL</span>
                {{ t(`${TB}.BUTTON_URL`) }}
                <span class="text-[11px] text-n-slate-9 ml-auto"
                  >{{ urlCount }}/2</span
                >
              </button>
              <button
                class="wa-btn-option"
                :disabled="phoneCount >= 1"
                @click="addButton('PHONE_NUMBER')"
              >
                <span class="wa-btn-type wa-btn-type--phone">TEL</span>
                {{ t(`${TB}.BUTTON_PHONE`) }}
                <span class="text-[11px] text-n-slate-9 ml-auto"
                  >{{ phoneCount }}/1</span
                >
              </button>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex justify-end gap-2.5 pt-1">
          <button class="btn-outline" @click="$emit('close')">
            {{ t(`${TB}.CANCEL`) }}
          </button>
          <button
            class="btn-primary"
            :disabled="!canSubmit || isSubmitting"
            @click="handleSubmit"
          >
            <span v-if="isSubmitting" class="wa-spin">&#x21BB;</span>
            {{ isEditMode ? t(`${TB}.UPDATE`) : t(`${TB}.SUBMIT`) }}
            &#x2192;
          </button>
        </div>
      </div>

      <!-- RIGHT: WhatsApp Preview -->
      <div ref="previewWrapper" class="wa-preview-wrapper">
        <div class="wa-phone">
          <div class="wa-phone-header">
            <div class="wa-phone-avatar">GK</div>
            <div>
              <div class="text-[13px] font-semibold text-white">
                GK Hub Internet
              </div>
              <div class="text-[11px] text-white/60">online</div>
            </div>
          </div>
          <div class="wa-phone-chat">
            <div class="wa-bubble">
              <div class="wa-bubble-content">
                <!-- Image header placeholder -->
                <div v-if="form.headerType === 'image'" class="wa-bubble-media">
                  &#x1F5BC;
                </div>
                <div v-if="form.headerType === 'video'" class="wa-bubble-media">
                  &#x1F3AC;
                </div>
                <div
                  v-if="form.headerType === 'document'"
                  class="wa-bubble-media wa-bubble-doc"
                >
                  <span class="text-xl text-[#667781]">&#x1F4C4;</span>
                  {{ form.headerFileName || 'document.pdf' }}
                </div>
                <!-- Text header -->
                <div
                  v-if="form.headerType === 'text' && form.headerText"
                  class="text-[13px] font-bold text-[#111b21] mb-0.5"
                >
                  {{ previewHeader }}
                </div>
                <!-- Body -->
                <!-- GK-HUB: Render WhatsApp formatting (bold/italic) in preview -->
                <div
                  v-if="form.bodyText"
                  class="wa-bubble-text"
                  v-html="previewBodyHtml"
                />
                <!-- Empty placeholder -->
                <div
                  v-if="!form.bodyText && form.headerType === 'none'"
                  class="text-xs text-[#667781] italic py-1"
                >
                  {{ t(`${TB}.PREVIEW_EMPTY`) }}
                </div>
                <!-- Footer -->
                <div
                  v-if="form.footerText"
                  class="text-[11px] text-[#667781] mt-1"
                >
                  {{ form.footerText }}
                </div>
                <!-- Time + checkmark -->
                <div
                  class="flex justify-end items-center gap-1 mt-1 text-[10px] text-[#667781]"
                >
                  14:32
                  <svg
                    width="16"
                    height="11"
                    viewBox="0 0 16 11"
                    fill="none"
                    class="text-[#53bdeb]"
                  >
                    <path
                      d="M11.07 0.94L4.5 7.51L1.93 4.94L0.87 6L4.5 9.64L12.13 2L11.07 0.94Z"
                      fill="currentColor"
                    />
                    <path
                      d="M14.07 0.94L7.5 7.51L6.5 6.52L5.43 7.58L7.5 9.64L15.13 2L14.07 0.94Z"
                      fill="currentColor"
                    />
                  </svg>
                </div>
              </div>
              <!-- GK-HUB: Preview buttons inside bubble (WhatsApp style with separator) -->
              <div v-if="form.buttons.length > 0" class="wa-buttons-wa">
                <div
                  v-for="(btn, idx) in form.buttons"
                  :key="idx"
                  class="wa-btn-wa"
                  :class="{ 'border-t border-white/5': idx > 0 }"
                >
                  <svg
                    v-if="btn.type === 'QUICK_REPLY'"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path d="M9 14l-4-4 4-4" />
                    <path d="M5 10h11a4 4 0 010 8h-1" />
                  </svg>
                  <svg
                    v-if="btn.type === 'URL'"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"
                    />
                    <polyline points="15 3 21 3 21 9" />
                    <line x1="10" y1="14" x2="21" y2="3" />
                  </svg>
                  <svg
                    v-if="btn.type === 'PHONE_NUMBER'"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"
                    />
                  </svg>
                  {{ btn.text || '...' }}
                </div>
              </div>
              <!-- GK-HUB: end preview buttons -->
            </div>
            <div class="wa-preview-label">{{ t(`${TB}.PREVIEW_LABEL`) }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ── Layout ────────────────────────────────────────────────────── */
.wa-builder-layout {
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 28px;
  align-items: start;
}
@media (max-width: 900px) {
  .wa-builder-layout {
    grid-template-columns: 1fr;
  }
  .wa-preview-wrapper {
    max-width: 380px;
    margin: 0 auto;
  }
}

/* ── Section title ─────────────────────────────────────────────── */
.wa-section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #6b7280;
  margin-bottom: 16px;
}

/* ── Form elements ─────────────────────────────────────────────── */
.wa-form-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
}
@media (max-width: 700px) {
  .wa-form-row {
    grid-template-columns: 1fr;
  }
}

.wa-form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
}
.wa-form-group:last-child {
  margin-bottom: 0;
}

/* ── Inputs ──────────────────────────────────────────────────────
   GK-HUB: !important p/ vencer CSS global do Chatwoot (input[type=text]
   force height 40px + font 16px por default). */
.wa-input {
  box-sizing: border-box !important;
  margin: 0 !important;
  padding: 8px 14px !important;
  border: 1px solid #e0e2e6 !important;
  border-radius: 8px !important;
  font-size: 13px !important;
  font-family: inherit;
  color: #1f2937;
  background: #f8f9fb;
  width: 100%;
  height: 38px !important;
  line-height: 1.5 !important;
  transition: border-color 0.15s ease;
}
.wa-input:focus {
  outline: none !important;
  border-color: #2e86c1 !important;
  box-shadow: 0 0 0 3px rgba(46, 134, 193, 0.12) !important;
}
/* GK-HUB: --sm para linhas de variáveis (alinha com badge .wa-var-tag de 26px) */
.wa-input--sm {
  padding: 4px 10px !important;
  font-size: 12px !important;
  height: 28px !important;
}
.wa-input--error {
  border-color: #ef4444 !important;
}
.wa-input:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.wa-textarea {
  resize: vertical;
  min-height: 100px;
  line-height: 1.7;
}

/* ── Select (ISP pattern) ──────────────────────────────────────── */
.isp-select-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  width: 100%;
}
.isp-select-arrow {
  position: absolute;
  right: 9px;
  top: 50%;
  transform: translateY(-50%);
  width: 14px;
  height: 14px;
  pointer-events: none;
  color: #9ca3af;
  flex-shrink: 0;
}
.isp-select {
  appearance: none !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  height: auto;
  margin: 0;
  font-size: 12px;
  font-family: inherit;
  line-height: 1.5;
  padding: 6px 30px 6px 10px;
  border-radius: 8px;
  outline: none;
  cursor: pointer;
  transition: border-color 150ms ease;
  background: none;
  background-color: #f8f9fb;
  color: #4b5563;
  border: 1px solid #e0e2e6;
}
.isp-select:hover {
  border-color: #cbd0d6;
}
.isp-select:focus {
  border-color: #2e86c1;
  box-shadow: 0 0 0 3px rgba(46, 134, 193, 0.12);
}
.isp-select:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ── Variable tags ─────────────────────────────────────────────── */
.wa-var-tag {
  padding: 4px 10px;
  background: rgba(93, 173, 226, 0.1);
  color: #5dade2;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

/* ── Upload area ───────────────────────────────────────────────── */
.wa-upload-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 28px;
  border: 2px dashed #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
}
.wa-upload-area:hover {
  border-color: #2e86c1;
  background: rgba(46, 134, 193, 0.04);
}
.wa-upload--done {
  border-color: #2dd4a8;
  background: rgba(45, 212, 168, 0.04);
}

/* ── Button type tags ──────────────────────────────────────────── */
.wa-btn-type {
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  flex-shrink: 0;
}
.wa-btn-type--qr {
  background: rgba(99, 102, 241, 0.15);
  color: #818cf8;
}
.wa-btn-type--url {
  background: rgba(93, 173, 226, 0.15);
  color: #5dade2;
}
.wa-btn-type--phone {
  background: rgba(45, 212, 168, 0.15);
  color: #2dd4a8;
}

/* ── Button items ──────────────────────────────────────────────── */
.wa-button-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
}

/* ── Button menu dropdown ──────────────────────────────────────── */
.wa-btn-menu {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  z-index: 10;
  min-width: 200px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}
.wa-btn-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  width: 100%;
  border: none;
  background: none;
  font-family: inherit;
  font-size: 13px;
  color: #1f2937;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;
}
.wa-btn-option:hover {
  background: rgba(0, 0, 0, 0.03);
}
.wa-btn-option:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

/* ── Buttons (brand gradient) ──────────────────────────────────── */
.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 18px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  font-family: inherit;
  transition: all 0.2s ease;
  white-space: nowrap;
  background: linear-gradient(135deg, #2e86c1 0%, #5dade2 100%);
  color: #fff;
  box-shadow: 0 2px 8px rgba(46, 134, 193, 0.25);
}
.btn-primary:hover {
  box-shadow: 0 4px 16px rgba(46, 134, 193, 0.35);
  transform: translateY(-1px);
}
.btn-primary:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.btn-outline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 18px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.2s ease;
  white-space: nowrap;
  background: transparent;
  border: 1px solid rgba(0, 0, 0, 0.1);
  color: #6b7280;
}
.btn-outline:hover {
  border-color: #2e86c1;
  color: #5dade2;
  background: rgba(46, 134, 193, 0.06);
}

.btn-outline-xs {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s ease;
  background: transparent;
  border: 1px solid rgba(0, 0, 0, 0.1);
  color: #6b7280;
}
.btn-outline-xs:hover {
  border-color: #2e86c1;
  color: #5dade2;
}
.btn-outline-xs:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

/* ── WhatsApp Phone Preview ────────────────────────────────────── */
/* GK-HUB: Preview follows scroll (JS-driven translateY, CSS sticky broken by ancestor overflow:auto) */
.wa-preview-wrapper {
  transition: transform 0.15s ease-out;
  will-change: transform;
  align-self: start;
}
.wa-phone {
  border-radius: 24px;
  overflow: hidden;
  border: 1px solid #e9edef;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  background: #efeae2;
}
.wa-phone-header {
  background: #075e54;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.wa-phone-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2e86c1, #5dade2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
}
.wa-phone-chat {
  min-height: 380px;
  padding: 16px;
  background: #efeae2;
  background-image: url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='20' cy='20' r='1' fill='rgba(0,0,0,0.03)'/%3E%3C/svg%3E");
}
.wa-bubble {
  max-width: 95%;
  margin-bottom: 4px;
}
.wa-bubble-content {
  background: #d9fdd3;
  border-radius: 8px 8px 0 8px;
  padding: 8px 10px;
  margin-left: auto;
  width: fit-content;
  max-width: 100%;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.06);
}
.wa-bubble-media {
  width: 100%;
  height: 160px;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 6px;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.3);
  font-size: 2rem;
}
.wa-bubble-doc {
  height: 56px;
  background: rgba(0, 0, 0, 0.04);
  flex-direction: row;
  gap: 10px;
  font-size: 12px;
  color: #667781;
}
.wa-bubble-text {
  font-size: 13px;
  color: #111b21;
  line-height: 1.5;
  word-break: break-word;
  white-space: pre-wrap;
}
.wa-code {
  font-family: monospace;
  font-size: 12px;
  background: rgba(0, 0, 0, 0.05);
  padding: 1px 4px;
  border-radius: 3px;
}
/* GK-HUB: Buttons stacked vertically inside bubble (Meta WhatsApp style) */
.wa-buttons-wa {
  margin-top: 4px;
  display: flex;
  flex-direction: column;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}
.wa-btn-wa {
  background: transparent;
  padding: 8px 12px;
  text-align: center;
  font-size: 14px;
  font-weight: 400;
  color: #0d7cff;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}
.wa-btn-wa:first-child {
  border-top: none;
}
.wa-preview-label {
  text-align: center;
  padding: 14px 16px;
  font-size: 12px;
  font-weight: 600;
  color: #667781;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  background: #f0f2f5;
  border-top: 1px solid #e9edef;
}

/* ── Spin animation ────────────────────────────────────────────── */
.wa-spin {
  animation: wa-spin 1s linear infinite;
  display: inline-block;
}
@keyframes wa-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>

<style>
/* GK-HUB: Builder dark mode overrides (unscoped)
   !important pq o light mode tem !important pra vencer CSS global do Chatwoot */
body.dark .wa-input {
  background: transparent !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
  color: rgba(255, 255, 255, 0.9) !important;
}
body.dark .wa-input:hover {
  border-color: rgba(255, 255, 255, 0.14) !important;
}
body.dark .isp-select {
  background-color: transparent;
  color: rgba(255, 255, 255, 0.72);
  border-color: rgba(255, 255, 255, 0.08);
}
body.dark .isp-select:hover {
  border-color: rgba(255, 255, 255, 0.14);
  background-color: rgba(255, 255, 255, 0.03);
}
body.dark .isp-select-arrow {
  color: #64748b;
}
body.dark .wa-upload-area {
  border-color: rgba(255, 255, 255, 0.08);
}
body.dark .wa-button-item {
  background: rgba(255, 255, 255, 0.03);
  border-color: rgba(255, 255, 255, 0.08);
}
body.dark .wa-btn-menu {
  background: #1e293b;
  border-color: rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
}
body.dark .wa-btn-option {
  color: rgba(255, 255, 255, 0.85);
}
body.dark .wa-btn-option:hover {
  background: rgba(255, 255, 255, 0.06);
}
body.dark .wa-section-title {
  color: #64748b;
}
body.dark .btn-outline,
body.dark .btn-outline-xs {
  border-color: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.6);
}
body.dark .btn-outline:hover,
body.dark .btn-outline-xs:hover {
  border-color: #2e86c1;
  color: #5dade2;
}
</style>
